'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  applyConsentUpdate,
  buildAcceptAllConsent,
  buildRejectAllConsent,
  consentChangedEventName,
  consentStorageKey,
  getConsent,
  saveConsent,
} from '../lib/consent';
import { resolveAppHref } from '../lib/portalRoutes';

export const ConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const syncConsentState = () => {
      const storedConsent = getConsent();
      setIsVisible(storedConsent === null);
    };

    syncConsentState();

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== consentStorageKey) {
        return;
      }
      syncConsentState();
    };

    window.addEventListener(consentChangedEventName, syncConsentState);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(consentChangedEventName, syncConsentState);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleAcceptAll = () => {
    saveConsent(buildAcceptAllConsent());
    applyConsentUpdate(getConsent());
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent(buildRejectAllConsent());
    applyConsentUpdate(getConsent());
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] px-3 pb-3 md:px-6 md:pb-6">
      <section className="pointer-events-auto mx-auto flex w-full max-w-4xl flex-col gap-3 rounded-2xl border border-base-300 bg-base-100/95 p-4 shadow-2xl backdrop-blur">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-base-content">Cookie and analytics preferences</p>
          <p className="text-sm text-[var(--color-fg-muted)]">
            We use analytics to improve product quality. You can accept or reject analytics tracking.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-xs">
            <Link href={resolveAppHref('/privacy')} className="link link-primary">
              Privacy Policy
            </Link>
            <Link href={resolveAppHref('/terms')} className="link link-primary">
              Terms
            </Link>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleRejectAll}>
              Reject
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleAcceptAll}>
              Accept
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
